/**
 * SiteConfigurationPanel — Site_Configuration_Consolidation SC2-3.
 *
 * Canonical surface for the three user-defined site-config layers:
 * Project Intent + Typed Preferences (per report type) + Site Context.
 *
 * Mounted on the SiteLandingPage Overview tab between the existing
 * site-stats area and the documents checklist. Same data feed as the
 * wizard / rerun inherit paths — when the user edits here, every
 * future report on the site picks the new value up automatically.
 *
 * Discipline highlights (full design in
 * docs/prompt-packs/Site_Configuration_Consolidation_Prompt.md SC2-3):
 *   - Three collapsible sections with status chips for instant
 *     glanceability (Set / Using defaults / N fields set / N chars set).
 *   - Inline edit forms (no modal). The flattened INTENT surface (Project
 *     Intent + Site Context, the Overview Intent tab) has NO manual Save /
 *     Cancel — its drafts are committed by the debounced auto-save below and
 *     an unobtrusive inline status line ("Saving…" / "Saved · {relative}" /
 *     "Read-only") shows progress (aurora-qa-4). The Report-Preferences
 *     dialog (`visibleSections=['prefs']`) is NOT auto-saved, so it keeps the
 *     unified Save / Cancel footer. The standalone (non-flattened) panel
 *     keeps its per-section Save / Cancel bars.
 *   - PERSIST-ON-CHANGE — a DEBOUNCED auto-save persists the in-flight draft
 *     (`intentDraft` / `contextDraft`) via the SAME store actions after a
 *     quiet period, so power-mix / intent edits survive a reload even with no
 *     explicit Save. It persists in place WITHOUT clearing the draft, dedupes
 *     against a per-section baseline, backs off while a manual (per-section)
 *     Save is in flight, drives the inline status indicator, and is fully
 *     suppressed when the site is `lockReadOnly`. See the auto-save effect.
 *   - Dirty-state dot in the section header when the form is edited
 *     but not yet committed; Esc = cancel.
 *   - Provenance footer reads `siteConfigUpdatedAt` /
 *     `siteConfigUpdatedBy` (stamped by `updateSiteProjectIntent` /
 *     `updateSiteTypedPreferencesDefault` / `updateSiteContext`).
 */
// R-131-OVERVIEW-UX-A
import React, { useCallback, useMemo, useState } from 'react';
import { sedToText } from '../../services/siteEvidence';
import { isLibraryIndexDoc } from '../../services/knowledge/libraryIndex';
import { ChevronDown, ChevronRight, FileText, Leaf, Building2, Zap, Droplets, Cable, Sparkles, AlertTriangle, MessageSquare, ClipboardList, DollarSign } from 'lucide-react';
import type { SiteEntity } from '../../types/site';
import type { PhasePowerComponentKey, ProjectIntent, TypedPreferencesByType } from '../../types/siteRag';
import type { ReportTypeId } from '../../config/reportTypes';
import { getReportType } from '../../config/reportTypes';
import { useStore } from '../../store';
import { ProjectIntentForm, summarizeProjectIntent, countSetIntentFields, emptyProjectIntent } from './ProjectIntentForm';
import { useIntentIntelligence } from '../../hooks/useIntentIntelligence';
import type { RankableIntentField } from '../../lib/intentIntelligence/nextBestInput';
import { PhaseGanttModal } from './PhaseGanttModal';
import { effectivePhases, withPhaseSourceCapacityMW } from '../../lib/projectIntent';
import { callUserFunction } from '../../lib/callUserFunction';
import { siteOwnerUid } from '../../lib/siteOwnerPath';
import { intentDecisionStateHash, undecidedProjectIntent, withoutBtmStrategy } from '../../lib/intent/undecidedProjectIntent';
import { isScreeningRecommendationMode } from '../../lib/intent/screeningIntentMode';
import { hashGenerationImpactValue } from '../../lib/generationImpact/integrity';
import type { ResetProjectIntentResult } from '../../types/undecidedIntent';
import type { PhasePlanApplication } from '../../types/projectIntentUpdate';
import { resolvePerSourceAvailability } from '../../lib/powerAvailability/resolvePerSourceAvailability';
import { recommendPhasePowerPlan } from '../../lib/powerAvailability/recommendPhasePowerPlan';
import {
  applyPhasePowerPlanToIntent,
  buildPhasePowerPlanSubHashes,
  phasePowerPlanStalenessReason,
} from '../../lib/powerAvailability/phasePowerPlanStamp';
import { maxPowerStudyStalenessReason } from '../../lib/generationImpact/maxPowerIntentStamp';
import { PreferencesFormFor } from './preferences';
import { SiteContextInput } from './SiteContextInput';
// SET-M3 — `EvidenceModeControl` no longer mounted here; the Site
// Evidence tab is the canonical editor. This panel renders a read-only
// label + jump link instead.
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ContextualHelpButton } from '../help/ContextualHelpButton';
import { toast } from 'sonner';
// Declutter (2026-06-10) — `effectiveWorkloadProfile` / `getWorkloadProfile`
// / `ImpactDetail` imports removed with the workload long-form blurb
// (Change 4). The Workload picker itself lives in ProjectIntentForm.

const REPORT_TYPE_ICON: Record<ReportTypeId, React.ElementType> = {
  'executive-summary':   FileText,
  'feasibility':        FileText,
  'environmental':      Leaf,
  'test-fit':           Building2,
  'power-availability': Zap,
  'water-resource':     Droplets,
  'interconnection':    Cable,
  'supply-chain-risk':  FileText,
  // PSR-1 (row 0.99290) — Public Sentiment Report.
  'public-sentiment':   MessageSquare,
  'custom-brief':       ClipboardList,
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report.
  'financial':          DollarSign,
};

const REPORT_TYPE_ORDER: ReportTypeId[] = [
  'feasibility',
  'environmental',
  'test-fit',
  'power-availability',
  'interconnection',
  'supply-chain-risk',
  // PSR-1 (row 0.99290) — Public Sentiment Report. Appended last so the
  // historical wizard ordering for active types is preserved.
  'public-sentiment',
];

/** Count non-undefined keys in a typed-preferences slice. Drives the "N fields set" chip. */
function countSetPrefFields(prefs: unknown): number {
  if (!prefs || typeof prefs !== 'object') return 0;
  let n = 0;
  for (const v of Object.values(prefs as Record<string, unknown>)) {
    if (v !== undefined && v !== null && v !== '') n++;
  }
  return n;
}

function humanizeAgo(iso: string | undefined): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface CollapsibleSectionProps {
  title: string;
  statusChip: React.ReactNode;
  /** Section is dirty (unsaved). Pulses the chip amber + shows a dot. */
  dirty: boolean;
  /** Section just saved successfully — pulses green for ~600ms. */
  flashSaved: boolean;
  expanded: boolean;
  onToggle: () => void;
  summary: React.ReactNode;
  children: React.ReactNode;
  testId: string;
  /** R-131-OVERVIEW-UX-B — render body only (host provides outer SectionCard). */
  flatten?: boolean;
  /**
   * SRW-4 (2026-05-19) — optional DOM `id` so external surfaces (e.g.
   * the rerun-wizard SSOT summary card's "Edit at site level →" link)
   * can deep-link to a specific section with a `#hash`. When set, the
   * <section> renders both the test-id and the anchor id.
   */
  anchorId?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title, statusChip, dirty, flashSaved, expanded, onToggle, summary, children, testId, anchorId, flatten = false,
}) => {
  const headingId = `${testId}-heading`;
  if (flatten) {
    return (
      <section id={anchorId} data-testid={testId} className="scroll-mt-4">
        {children}
      </section>
    );
  }
  return (
    <section
      id={anchorId}
      aria-labelledby={headingId}
      data-testid={testId}
      className={`border ${flashSaved ? 'border-success/60 bg-success/5' : 'border-border'} transition-colors duration-300 scroll-mt-4`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`${testId}-body`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-state-hover text-left"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {expanded
            ? <ChevronDown className="w-3.5 h-3.5 text-text-muted shrink-0" />
            : <ChevronRight className="w-3.5 h-3.5 text-text-muted shrink-0" />}
          <h3
            id={headingId}
            className="text-sm font-bold text-text-primary truncate"
          >
            {title}
          </h3>
          {dirty && (
            <span
              className="inline-flex items-center gap-1 text-xs text-warning"
              data-testid={`${testId}-dirty-dot`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-warning" /> Unsaved
            </span>
          )}
        </div>
        <div className="shrink-0">{statusChip}</div>
      </button>
      {!expanded && (
        <div className="px-4 pb-3 text-xs text-text-secondary">
          {summary}
        </div>
      )}
      {expanded && (
        <div id={`${testId}-body`} data-testid={`${testId}-body`} className="px-4 pb-4 pt-1 space-y-3 border-t border-border-subtle">
          {children}
        </div>
      )}
    </section>
  );
};

interface StatusChipProps {
  tone: 'set' | 'muted' | 'warning';
  label: string;
  testId?: string;
}

// R-131-OVERVIEW-UX-A — sentence-case status pills via shared Badge primitive.
const StatusChip: React.FC<StatusChipProps> = ({ tone, label, testId }) => {
  const cls =
    tone === 'set'     ? 'border-accent/40 bg-accent/10 text-accent hover:bg-accent/10'
    : tone === 'warning' ? 'border-warning/40 bg-warning/10 text-warning hover:bg-warning/10'
    : 'border-border-subtle bg-state-hover text-text-muted hover:bg-state-hover';
  return (
    <Badge
      variant="outline"
      data-testid={testId}
      className={`font-normal normal-case tracking-normal px-1.5 py-0 text-xs ${cls}`}
    >
      {label}
    </Badge>
  );
};

export interface SiteConfigurationPanelProps {
  site: SiteEntity;
  /** Optional test-id prefix so the panel can be co-mounted in tests. */
  testIdPrefix?: string;
  /**
   * R-131-OVERVIEW-UX-B — when set, only render these sections (Overview zone 3).
   */
  visibleSections?: Array<'intent' | 'prefs' | 'context' | 'evidence'>;
  /** R-131-OVERVIEW-UX-B — hide the outer "Site Configuration" chrome. */
  hideShell?: boolean;
  /** R-131-OVERVIEW-UX-B — skip inner CollapsibleSection headers (host provides SectionCard). */
  flattenSections?: boolean;
  /** R-131-OVERVIEW-UX-B — pre-expand a section on mount. */
  defaultExpandedSection?: ExpandedKey;
  /**
   * DR2 — previously fired when the user clicked the "Add site context"
   * breadcrumb. Declutter (2026-06-10) removed that breadcrumb (Change 5;
   * the Context stepper step replaces it), so this callback is no longer
   * wired to any affordance in this panel. Retained on the interface for
   * caller back-compat — callers may still pass it harmlessly.
   */
  onOpenSiteContext?: () => void;
  /**
   * SET-M3 — fired when the user clicks "Change in Site Evidence →"
   * inside the Evidence Mode section. The Overview panel no longer
   * hosts the editable `EvidenceModeControl`; the Site Evidence tab
   * is the single source of truth. Hosts wire this to their tab
   * switcher (e.g. `setActiveTab('evidence')`). When absent the link
   * is hidden — keeps the panel safe to mount in isolation (tests,
   * wizard parity surfaces) without forcing a callback.
   */
  onJumpToEvidenceTab?: () => void;
  /**
   * Focused-Stepper layout for the Intent section's `ProjectIntentForm`
   * ONLY. Forwarded straight through as `ProjectIntentForm stepper={…}`.
   * Default `false` keeps the existing single-scroll intent layout. Does
   * not affect any other section (Context renders unchanged below it).
   */
  intentStepper?: boolean;
  /**
   * Stepper "Done" destination — forwarded to `ProjectIntentForm
   * onStepperDone`. The site host wires the guided loop's finish (record +
   * return to Path to GO). Absent → the legacy disabled Done button.
   */
  onIntentStepperDone?: () => void;
  /**
   * Legacy host compatibility. Study-area review now lives in the Map tab;
   * Intent does not render a second land-screening surface.
   */
  onReviewStudyArea?: () => void;
  /**
   * PERSIST-ON-CHANGE — when `true`, the site is locked for the current
   * user (held by another teammate, no edit grant) and ALL writes from
   * this panel are suppressed, including the new debounced auto-save.
   * Threaded from `SiteLandingPage`'s `lockReadOnly` so the auto-save can
   * never bypass the lock the host already enforces on the manual
   * affordances. Default `false` keeps every existing mount editable.
   */
  lockReadOnly?: boolean;
  /** Limit rendered prefs to specific report types. */
  filterReportTypes?: ReportTypeId[];
  /**
   * Controlled Intent view. When supplied the host owns whether the screening
   * brief or the Design & report assumptions view is shown (so a "Do this
   * next" deep link can open the Design view); absent → local state.
   */
  intentInputStage?: 'screening' | 'detailed';
  onIntentInputStageChange?: (stage: 'screening' | 'detailed') => void;
  /**
   * Rendered at the top of the Design & report assumptions view (the Stage 2
   * decision checklist). `applyIntent` feeds the SAME draft + auto-save path
   * as the form's own edits and accepted suggestions — never a second writer.
   */
  renderDesignHeader?: (ctx: {
    intent: ProjectIntent;
    applyIntent: (next: ProjectIntent) => void;
    activeStep?: string;
    onSelectStep?: (stepId: string) => void;
  }) => React.ReactNode;
  /** Same canonical draft writer, available after completed screening. */
  renderScreeningHeader?: (ctx: {
    intent: ProjectIntent;
    applyIntent: (next: ProjectIntent) => void;
  }) => React.ReactNode;
  /** Rendered above the screening brief (e.g. "Stage 2 decisions ready"). */
  screeningNotice?: React.ReactNode;
}

type ExpandedKey = 'intent' | 'prefs' | 'context' | 'evidence' | null;
type PrefRowKey = ReportTypeId | null;

export const SiteConfigurationPanel: React.FC<SiteConfigurationPanelProps> = ({
  site,
  testIdPrefix = 'site-config-panel',
  visibleSections,
  hideShell = false,
  flattenSections = false,
  defaultExpandedSection,
  onOpenSiteContext,
  onJumpToEvidenceTab,
  intentStepper = false,
  onIntentStepperDone,
  onReviewStudyArea,
  lockReadOnly = false,
  filterReportTypes,
  intentInputStage: controlledInputStage,
  onIntentInputStageChange,
  renderDesignHeader,
  renderScreeningHeader,
  screeningNotice,
}) => {
  // Declutter (2026-06-10) — `onOpenSiteContext` lost its consumer with the
  // removed "Add site context" breadcrumb (Change 5). Held inert so the
  // prop stays on the interface without an unused-var warning.
  void onOpenSiteContext;
  void onReviewStudyArea;
  const updateSiteProjectIntent = useStore(s => s.updateSiteProjectIntent);
  const updateSiteTypedPreferencesDefault = useStore(s => s.updateSiteTypedPreferencesDefault);
  const updateSiteContext = useStore(s => s.updateSiteContext);
  const fetchSites = useStore(s => s.fetchSites);
  const retryExtractClaims = useStore(s => s.retryExtractClaims);
  // DR2 — `updateSite` no longer needed here; the description writer is gone.
  // SET-M3 — `setSiteEvidenceMode` is no longer dispatched from this
  // panel either. The Site Evidence tab's EvidenceModeControl mount is
  // the single writer; this panel is read-only for Evidence Mode.

  // RWX-4 — read the `?siteConfig=<section>` URL param on mount so the
  // "Edit at site level →" link from the rerun panel (RWX-2) lands the
  // user on the right section pre-expanded. Param values mirror the
  // `ExpandedKey` union; unknown values fall through to null. A bare
  // `?siteConfig=` (no value) is also tolerated to keep the link
  // forgiving. Scoped to the Overview-tab mount of this panel — the
  // wizard mount of `SiteConfigurationPanel` doesn't consume the param
  // because the wizard URL doesn't carry it.
  const readSiteConfigParam = (): ExpandedKey => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = new URLSearchParams(window.location.search).get('siteConfig');
      if (raw === 'intent' || raw === 'prefs' || raw === 'context' || raw === 'evidence') {
        return raw;
      }
      return null;
    } catch { return null; }
  };

  // Section + per-prefs-row expansion state. Only one section at a time
  // keeps the panel compact; per-prefs-row is independent because
  // expanding one report-type sub-row inside the prefs section is a
  // natural "drill in" rather than a section switch.
  const [expanded, setExpanded] = useState<ExpandedKey>(
    // Auto-expand the Site Context section on a fresh/empty site so the
    // highest-priority user-declared ground-truth input ("What we know about
    // this site") is surfaced immediately instead of hidden behind an "Empty"
    // chip. Once the user has entered context text it stays collapsed unless
    // explicitly opened (or deep-linked via ?siteConfig=).
    defaultExpandedSection ?? readSiteConfigParam() ?? (site.siteContext?.text?.trim() ? null : 'context'),
  );
  const [prefsRowExpanded, setPrefsRowExpanded] = useState<PrefRowKey>(filterReportTypes?.[0] ?? null);

  // RWX-4 — popstate listener so browser back/forward repositions the
  // expanded section. Matches the SiteLandingPage tab popstate handler
  // pattern (kept local because SiteConfigurationPanel doesn't share
  // its expansion state through the URL writer — read-only deep-link).
  React.useEffect(() => {
    const onPop = () => {
      const next = readSiteConfigParam();
      // Only update when the param resolves to a meaningful value; a
      // pop to a URL WITHOUT `?siteConfig=` should NOT auto-collapse
      // a section the user manually opened.
      if (next) setExpanded(next);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Per-section dirty drafts. `null` ⇒ no draft, the section header
  // shows the saved value.
  const initialIntent = site.projectIntent;
  const [intentDraft, setIntentDraft] = useState<ProjectIntent | null>(null);
  const intentDraftBaselineRef = React.useRef<string | undefined>(undefined);
  const phasePlanApplicationRef = React.useRef<PhasePlanApplication | undefined>(undefined);
  const stageIntentEdit = useCallback((next: ProjectIntent) => {
    if (lockReadOnly || intentResetInFlightRef.current) return;
    intentDraftBaselineRef.current ??= intentDecisionStateHash(site);
    // An intervening edit retires the unsaved recommendation recipe. A later
    // Apply must recompute against that edit before its stamp can be adopted.
    if (phasePlanApplicationRef.current) {
      next = { ...next };
      delete next.phasePowerPlan;
      next.phases = next.phases?.map(phase => {
        const original = site.projectIntent?.phases?.find(value => value.id === phase.id);
        let edited = phase;
        for (const [key, params] of Object.entries(phase.powerSourceConfig?.componentParams ?? {})) {
          const prior = original?.powerSourceConfig?.componentParams?.[key as PhasePowerComponentKey];
          if (params.capacitySource === 'phase-power-plan'
            && hashGenerationImpactValue(params) !== hashGenerationImpactValue(prior ?? null)) {
            edited = withPhaseSourceCapacityMW(edited, key as PhasePowerComponentKey, params.capacityMW ?? null);
          }
        }
        return edited;
      });
      phasePlanApplicationRef.current = undefined;
    }
    setIntentDraft(next);
  }, [lockReadOnly, site]);
  const [localInputStage, setLocalInputStage] = useState<'screening' | 'detailed'>('screening');
  const [intentSubTab, setIntentSubTab] = useState<'intent' | 'recommended-path'>('intent');
  const intentInputStage = controlledInputStage ?? localInputStage;
  const setIntentInputStage = useCallback((stage: 'screening' | 'detailed') => {
    setIntentSubTab('intent');
    if (onIntentInputStageChange) onIntentInputStageChange(stage);
    else setLocalInputStage(stage);
  }, [onIntentInputStageChange]);
  React.useEffect(() => {
    if (controlledInputStage === 'detailed') setIntentSubTab('intent');
  }, [controlledInputStage]);
  // DR2 — `descriptionDraft` + `editingDescription` removed. The
  // freeform brief lives on `siteContext` now; any legacy
  // `site.description` is auto-migrated by DR1's lazy migrator into
  // a `general/brief` SiteContext claim.
  // Declutter (2026-06-10) — `showIntentImpact` removed with the workload
  // "View what this changes" disclosure (Change 4).
  const [prefsDraft, setPrefsDraft] = useState<TypedPreferencesByType | null>(null);
  const [contextDraft, setContextDraft] = useState<string | null>(null);
  const [intentResetPending, setIntentResetPending] = useState(false);
  const [intentResetMessage, setIntentResetMessage] = useState('');
  const intentResetInFlightRef = React.useRef(false);
  const intentSubjectRef = React.useRef({ siteId: site.id, mounted: true });
  // Replace the subject during render so an old asynchronous continuation is
  // fenced even before the site-change effect clears this site's draft UI.
  if (intentSubjectRef.current.siteId !== site.id) intentSubjectRef.current = { siteId: site.id, mounted: true };
  React.useEffect(() => {
    intentSubjectRef.current.mounted = true;
    return () => { intentSubjectRef.current = { siteId: intentSubjectRef.current.siteId, mounted: false }; };
  }, []);
  const [manualWritePending, setManualWritePending] = useState(false);
  const saveAllInFlightRef = React.useRef(false);

  // PERSIST-ON-CHANGE — debounced auto-save bookkeeping (full mechanism +
  // rationale at the auto-save effect below). Declared here so the manual
  // Save callbacks can flip `manualSaveInFlightRef` / reset the baselines.
  //   - lastAutoSaved*Ref: JSON of the last value we auto-persisted per
  //     section (`undefined` ⇒ none yet) — dedupe guard.
  //   - manualSaveInFlightRef: a footer Save is mid-write ⇒ auto-save backs off.
  //   - autoSaveInFlightRef: an auto-save is mid-write ⇒ no concurrent re-fire.
  const lastAutoSavedIntentRef = React.useRef<string | undefined>(undefined);
  const lastAutoSavedContextRef = React.useRef<string | undefined>(undefined);
  // A per-section Save (standalone panel, `!flattenSections`) is mid-write ⇒
  // the debounced auto-save backs off so the same draft is not double-written.
  const manualSaveInFlightRef = React.useRef(false);
  const autoSaveInFlightRef = React.useRef(false);
  // AUTOSAVE-STATUS — drives the unobtrusive inline indicator that REPLACED
  // the old unified Save/Cancel footer (aurora-qa-4). `'idle'` shows nothing,
  // `'saving'` shows "Saving…", `'saved'` shows "Saved · {relative}" using
  // `autoSavedAt`. Locked sites render the read-only label and never write.
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const [failedAutoSaveSection, setFailedAutoSaveSection] = useState<'intent' | 'context' | null>(null);
  const [autoSavedAt, setAutoSavedAt] = useState<number | null>(null);
  const [autoSaveRelease, setAutoSaveRelease] = useState(0);

  // Drop unsaved drafts when the host switches sites (SiteLandingPage
  // remounts via `key={site.id}`, but belt-and-suspenders for any
  // future mount that reuses this panel instance).
  React.useEffect(() => {
    setIntentDraft(null);
    intentDraftBaselineRef.current = undefined;
    phasePlanApplicationRef.current = undefined;
    // Local view only: a controlled host resets its own view per site, and
    // resetting it here on mount would undo a deep link into the Design view.
    setLocalInputStage('screening');
    setPrefsDraft(null);
    setContextDraft(null);
    setIntentResetMessage('');
    setIntentResetPending(false);
    intentResetInFlightRef.current = false;
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setFailedAutoSaveSection(null);
    setAutoSavedAt(null);
    setDismissedIntentFields(new Set<RankableIntentField>());
    // Reset the auto-save baselines so a fresh site never inherits the
    // prior site's "already persisted" markers.
    lastAutoSavedIntentRef.current = undefined;
    lastAutoSavedContextRef.current = undefined;
  }, [site.id]);

  // Section-level flash-saved trigger. Pulses green for 600ms.
  const [flashSaved, setFlashSaved] = useState<ExpandedKey>(null);
  const triggerFlash = useCallback((key: ExpandedKey) => {
    setFlashSaved(key);
    setTimeout(() => setFlashSaved(prev => (prev === key ? null : prev)), 600);
  }, []);

  // ── Intent section ──────────────────────────────────────────────
  const intentValue = intentDraft ?? initialIntent;
  const intentSetCount = countSetIntentFields(intentValue);
  const intentDirty = intentDraft !== null;

  // ── SLICE 5 — Intent Intelligence ───────────────────────────────
  // Live deterministic ∪ LLM suggestions + the "what to fill next"
  // ranking for the LIVE intent (draft when editing, else the saved
  // value). The LLM layer is content-hash-gated + debounced inside the
  // hook; the deterministic layer is free. We feed the site-context
  // prose + claims + hydrated SED so the inference context is grounded.
  // No new store action — accepts merge into `intentDraft` below.
  // The LLM inference context reads the COMPILED SED as prose plus the
  // uploaded-doc summaries — pre-this, `sed` was hardcoded `undefined` and
  // docs were never passed, so inference saw only the free-text context and
  // ignored everything the user had uploaded. Both are memoized (sedToText
  // walks the whole SED) and both feed the hook's content-hash gate, so a
  // new upload / SED recompile re-arms inference exactly once.
  const sedProseForInference = useMemo(() => {
    try {
      return site.sed ? sedToText(site.sed) : undefined;
    } catch {
      return undefined; // serialization is best-effort context, never a blocker
    }
  }, [site.sed]);
  const docsForInference = useMemo(
    () => (site.documents ?? [])
      .filter(d => !isLibraryIndexDoc(d) && (d as { deletedAt?: string | null }).deletedAt == null)
      .slice(0, 20)
      .map(d => ({ fileName: d.fileName, summary: d.summary, tags: d.tags })),
    [site.documents],
  );
  const { suggestions: intentSuggestions, nextBestInput } = useIntentIntelligence({
    intent: intentValue,
    site,
    sed: site.sed ?? null,
    stage: intentInputStage,
    disableInference: Boolean(site.draftOnly),
    inferenceContext: {
      sed: sedProseForInference,
      siteContextProse: site.siteContext?.text,
      claims: (site.siteContext?.claims ?? []).map(c => ({
        claim: c.claim,
        claimedValue: c.claimedValue,
      })),
      docs: docsForInference,
    },
  });
  // Per-session dismissed suggestions — cleared on a site switch (below).
  const [dismissedIntentFields, setDismissedIntentFields] = useState<ReadonlySet<RankableIntentField>>(
    () => new Set<RankableIntentField>(),
  );
  const intentSummary = useMemo(() => summarizeProjectIntent(intentValue), [intentValue]);
  // PP4 — phased rollout summary chip + Gantt modal.
  const phasedView = useMemo(() => effectivePhases(intentValue), [intentValue]);
  // ── Phase power plan (host-computed — the form has no SED/scenario access).
  // Pure array math over ≤8 sources × phases; cheap enough for keystroke memos.
  const phasePowerAvailability = useMemo(
    () => resolvePerSourceAvailability({
      sed: site.sed ?? null,
      promotedScenario: site.promotedGenerationScenarioSnapshot ?? null,
    }),
    [site.sed, site.promotedGenerationScenarioSnapshot],
  );
  const phasePowerEnvelope = useMemo(() => {
    const stamp = intentValue?.maxPowerStudy;
    if (!stamp) return null;
    return {
      facilityMW: stamp.facilityMW ?? null,
      stalenessReason: maxPowerStudyStalenessReason({
        intent: intentValue,
        promotedScenario: site.promotedGenerationScenarioSnapshot ?? null,
        storedStudy: site.maxPowerStudySnapshot ?? null,
        sed: site.sed ?? null,
      }),
    };
  }, [intentValue, site.promotedGenerationScenarioSnapshot, site.maxPowerStudySnapshot, site.sed]);
  const phasePowerPlan = useMemo(
    () => recommendPhasePowerPlan({
      intent: intentValue,
      availability: phasePowerAvailability,
      maxPowerEnvelope: phasePowerEnvelope,
    }),
    [intentValue, phasePowerAvailability, phasePowerEnvelope],
  );
  const phasePowerSubHashes = useMemo(
    () => intentValue
      ? buildPhasePowerPlanSubHashes({
          intent: intentValue,
          availability: phasePowerAvailability,
          envelopeHash: intentValue.maxPowerStudy?.studyHash ?? 'none',
        })
      : null,
    [intentValue, phasePowerAvailability],
  );
  const phasePowerPlanStaleReason = useMemo(
    () => phasePowerSubHashes
      ? phasePowerPlanStalenessReason({ intent: intentValue, currentSubHashes: phasePowerSubHashes })
      : null,
    [intentValue, phasePowerSubHashes],
  );
  const handleApplyPhasePowerPlan = useCallback((phaseId: string) => {
    if (lockReadOnly || intentResetInFlightRef.current || !intentValue || phasePowerPlan.status !== 'available' || !phasePowerSubHashes) return;
    try {
      const appliedAt = new Date().toISOString();
      const next = applyPhasePowerPlanToIntent({
        intent: intentValue,
        plan: phasePowerPlan,
        phaseIds: [phaseId],
        subHashes: phasePowerSubHashes,
        now: appliedAt,
      });
      intentDraftBaselineRef.current ??= intentDecisionStateHash(site);
      const pending = phasePlanApplicationRef.current;
      phasePlanApplicationRef.current = {
        preApplyIntent: pending?.preApplyIntent ?? JSON.parse(JSON.stringify(intentValue)),
        steps: [...(pending?.steps ?? []), { phaseIds: [phaseId], appliedAt }],
      };
      setIntentDraft(next);
    } catch {
      toast.error('Could not apply the power plan to this phase.');
    }
  }, [lockReadOnly, intentValue, phasePowerPlan, phasePowerSubHashes, site]);
  const [showPhaseGantt, setShowPhaseGantt] = useState(false);
  // Declutter (2026-06-10) — the long-form workload blurb (`workloadEntry`)
  // + its ImpactDetail disclosure were removed (Change 4). The Workload
  // profile PICKER stays inside ProjectIntentForm.

  const saveIntent = useCallback(async (opts?: { silent?: boolean }) => {
    if (!intentDraft || lockReadOnly || intentResetInFlightRef.current || manualSaveInFlightRef.current || autoSaveInFlightRef.current) return;
    const subject = intentSubjectRef.current;
    // PERSIST-ON-CHANGE — flag the manual write so the debounced auto-save
    // can't fire a concurrent (double) write for the same draft.
    manualSaveInFlightRef.current = true;
    setManualWritePending(true);
    try {
      const recipe = phasePlanApplicationRef.current;
      await updateSiteProjectIntent(site.id, intentDraft, {
        expectedDecisionHash: intentDraftBaselineRef.current,
        phasePlanApplication: recipe,
      });
      if (intentSubjectRef.current !== subject || !subject.mounted) return;
      const saved = useStore.getState().siteRag.sites.find(candidate => candidate.id === site.id);
      if (saved) intentDraftBaselineRef.current = intentDecisionStateHash(saved);
      setIntentDraft(previous => {
        if (previous && hashGenerationImpactValue(previous) !== hashGenerationImpactValue(intentDraft)) return previous;
        intentDraftBaselineRef.current = undefined;
        if (phasePlanApplicationRef.current === recipe) phasePlanApplicationRef.current = undefined;
        return null;
      });
      // The committed draft is now the saved value — drop the auto-save
      // baseline so a later identical re-edit is still auto-persisted.
      lastAutoSavedIntentRef.current = undefined;
      triggerFlash('intent');
      if (!opts?.silent) toast.success('Site project intent saved');
    } catch (e) {
      if (!opts?.silent && intentSubjectRef.current === subject && subject.mounted) toast.error(`Couldn't save project intent: ${(e as Error)?.message ?? 'unknown error'}`);
      throw e;
    } finally {
      manualSaveInFlightRef.current = false;
      if (intentSubjectRef.current.mounted) setManualWritePending(false);
    }
  }, [intentDraft, lockReadOnly, site.id, updateSiteProjectIntent, triggerFlash]);

  const resetIntentChoices = useCallback(async () => {
    if (lockReadOnly || intentResetInFlightRef.current || manualSaveInFlightRef.current || autoSaveInFlightRef.current || saveAllInFlightRef.current) return;
    const subject = intentSubjectRef.current;
    const isCurrentSubject = () => intentSubjectRef.current === subject && subject.mounted;
    intentResetInFlightRef.current = true;
    setIntentResetPending(true);
    setIntentResetMessage('');
    try {
      const { auth } = await import('../../firebase');
      if (!isCurrentSubject()) return;
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Sign in before resetting project choices.');
      const ownerUid = siteOwnerUid(site, uid);
      const current = useStore.getState().siteRag.sites.find(candidate => candidate.id === site.id);
      if (!current || intentDecisionStateHash(current) !== intentDecisionStateHash(site)) {
        throw new Error('Project choices changed. Refresh and review them before resetting.');
      }
      // Preserve pending operator statements through the existing normal save
      // writer before the atomic reset. The request remains tied to the choices
      // originally displayed: a concurrent newer selection must never be lost.
      if (intentDraft) await updateSiteProjectIntent(site.id, intentDraft, {
        expectedDecisionHash: intentDraftBaselineRef.current,
        phasePlanApplication: phasePlanApplicationRef.current,
      });
      if (!isCurrentSubject()) return;
      const expectedIntent = useStore.getState().siteRag.sites.find(candidate => candidate.id === site.id)?.projectIntent;
      const expectedSite = { ...site, projectIntent: expectedIntent };
      const expectedDecisionHash = intentDecisionStateHash(expectedSite);
      const result = await callUserFunction<ResetProjectIntentResult>('resetProjectIntent', {
        siteId: site.id, ownerUid, expectedDecisionHash,
      });
      const expectedNext = { projectIntent: undecidedProjectIntent(expectedIntent),
        typedPreferencesDefaults: { 'power-availability': withoutBtmStrategy(site.typedPreferencesDefaults?.['power-availability']) },
        stage2DecisionReview: {} };
      if (result.siteId !== site.id || result.ownerUid !== ownerUid
        || hashGenerationImpactValue(result.projectIntent) !== hashGenerationImpactValue(expectedNext.projectIntent)
        || hashGenerationImpactValue(result.powerAvailabilityPreferences) !== hashGenerationImpactValue(expectedNext.typedPreferencesDefaults['power-availability'])
        || result.decisionHash !== intentDecisionStateHash(expectedNext)) {
        throw new Error('The saved reset response could not be verified. Refresh the site before editing again.');
      }
      // Apply only the authenticated, exact-CAS result to the reader cache. This
      // is not a client write to protected Firestore fields. A newer cached
      // choice is left alone and reloaded through the normal site reader.
      useStore.setState(state => ({ siteRag: { ...state.siteRag, sites: state.siteRag.sites.map(candidate => {
        if (candidate.id !== site.id || intentDecisionStateHash(candidate) !== expectedDecisionHash) return candidate;
        const next: SiteEntity = { ...candidate, projectIntent: result.projectIntent,
          typedPreferencesDefaults: { ...(candidate.typedPreferencesDefaults ?? {}), 'power-availability': result.powerAvailabilityPreferences },
          stage2DecisionReview: {}, siteConfigUpdatedAt: result.resetAt, updatedAt: result.resetAt, lastDataChangeAt: result.resetAt };
        delete next.generationAssessmentSelection;
        delete next.generationProjectInputBinding;
        return next;
      }) } }));
      // Trusted server A may finish while the panel now displays B. Its reader
      // cache is still updated by exact CAS, but B's draft/status is untouched.
      useStore.getState()._staleStampSiteComposite(site.id);
      void useStore.getState().recomputeEvidenceReadiness(site.id);
      if (!isCurrentSubject()) return;
      setIntentDraft(null);
      intentDraftBaselineRef.current = undefined;
      phasePlanApplicationRef.current = undefined;
      // Keep unrelated pending report setup. Only its chosen BTM strategy is
      // cleared, so a later preferences save cannot resurrect that choice.
      setPrefsDraft(previous => previous ? { ...previous,
        'power-availability': withoutBtmStrategy(previous['power-availability']) } : null);
      lastAutoSavedIntentRef.current = undefined;
      triggerFlash('intent');
      setIntentResetMessage('Reset saved as undecided. Saved assessments remain available to review and explicitly select again.');
      await fetchSites();
    } catch (error) {
      if (!isCurrentSubject()) return;
      const message = error instanceof Error ? error.message : 'Could not reset project choices.';
      setIntentResetMessage(message);
      toast.error(message);
    } finally {
      if (isCurrentSubject()) {
        intentResetInFlightRef.current = false;
        setIntentResetPending(false);
      }
    }
  }, [lockReadOnly, site, intentDraft, updateSiteProjectIntent, fetchSites, triggerFlash]);

  const cancelIntent = useCallback(() => {
    if (intentResetInFlightRef.current || manualSaveInFlightRef.current) return;
    setIntentDraft(null);
    intentDraftBaselineRef.current = undefined;
    phasePlanApplicationRef.current = undefined;
    // Cancel reverts the draft — clear the auto-save baseline so it doesn't
    // suppress a future identical edit. (Already-auto-persisted data stays
    // on the site; cancel only discards the in-flight draft.)
    lastAutoSavedIntentRef.current = undefined;
  }, []);

  // ── Typed Preferences section ───────────────────────────────────
  const livePrefs = prefsDraft ?? site.typedPreferencesDefaults ?? {};
  const prefsDirty = prefsDraft !== null;
  const prefsCustomizedCount = useMemo(
    () => REPORT_TYPE_ORDER.filter(rt => countSetPrefFields(livePrefs[rt]) > 0).length,
    [livePrefs],
  );

  // We commit per-report-type so the audit log + Firestore writes carry
  // the exact slot that changed. Save iterates over the dirty subset.
  const savePrefs = useCallback(async (opts?: { silent?: boolean }) => {
    if (!prefsDraft || lockReadOnly || intentResetInFlightRef.current || manualSaveInFlightRef.current || autoSaveInFlightRef.current) return;
    const subject = intentSubjectRef.current;
    manualSaveInFlightRef.current = true;
    setManualWritePending(true);
    try {
      const base = site.typedPreferencesDefaults ?? {};
      const dirtyTypes = REPORT_TYPE_ORDER.filter(rt => {
        // Use JSON identity rather than === since prefs objects can be
        // freshly constructed by the dispatcher.
        return JSON.stringify(prefsDraft[rt] ?? {}) !== JSON.stringify(base[rt] ?? {});
      });
      for (const rt of dirtyTypes) {
        const slot = prefsDraft[rt];
        if (slot) {
          await updateSiteTypedPreferencesDefault(site.id, rt, slot);
        }
      }
      if (intentSubjectRef.current !== subject || !subject.mounted) return;
      setPrefsDraft(null);
      setPrefsRowExpanded(null);
      triggerFlash('prefs');
      if (!opts?.silent) {
        toast.success(dirtyTypes.length === 1
          ? `Site default for ${getReportType(dirtyTypes[0]).shortLabel} saved`
          : `Site defaults saved (${dirtyTypes.length} report types)`);
      }
    } catch (e) {
      if (!opts?.silent && intentSubjectRef.current === subject && subject.mounted) toast.error(`Couldn't save preferences: ${(e as Error)?.message ?? 'unknown error'}`);
      throw e;
    } finally {
      manualSaveInFlightRef.current = false;
      if (intentSubjectRef.current.mounted) setManualWritePending(false);
    }
  }, [prefsDraft, lockReadOnly, site.id, site.typedPreferencesDefaults, updateSiteTypedPreferencesDefault, triggerFlash]);

  const cancelPrefs = useCallback(() => {
    if (intentResetInFlightRef.current || manualSaveInFlightRef.current) return;
    setPrefsDraft(null);
    setPrefsRowExpanded(null);
  }, []);

  // ── Site Context section ────────────────────────────────────────
  const contextText = contextDraft ?? site.siteContext?.text ?? '';
  const contextDirty = contextDraft !== null;
  const contextLen = contextText.length;
  const contextSetTone: 'set' | 'muted' = contextLen >= 50 ? 'set' : 'muted';
  const contextClaimCount = site.siteContext?.claims.length ?? 0;
  const contextUnacknowledgedConflicts = useMemo(() => {
    const ack = new Set(site.siteContext?.acknowledgedConflictIds ?? []);
    return (site.siteContext?.conflicts ?? []).filter(c => !ack.has(c.id) && c.severity !== 'low').length;
  }, [site.siteContext]);

  const saveContext = useCallback(async (opts?: { silent?: boolean }) => {
    if (contextDraft === null || lockReadOnly || intentResetInFlightRef.current || manualSaveInFlightRef.current || autoSaveInFlightRef.current) return;
    const subject = intentSubjectRef.current;
    // PERSIST-ON-CHANGE — guard against a concurrent debounced auto-save.
    manualSaveInFlightRef.current = true;
    setManualWritePending(true);
    try {
      await updateSiteContext(site.id, contextDraft);
      if (intentSubjectRef.current !== subject || !subject.mounted) return;
      setContextDraft(null);
      lastAutoSavedContextRef.current = undefined;
      triggerFlash('context');
      if (!opts?.silent) toast.success('Site context saved');
    } catch (e) {
      if (!opts?.silent && intentSubjectRef.current === subject && subject.mounted) toast.error(`Couldn't save site context: ${(e as Error)?.message ?? 'unknown error'}`);
      throw e;
    } finally {
      manualSaveInFlightRef.current = false;
      if (intentSubjectRef.current.mounted) setManualWritePending(false);
    }
  }, [contextDraft, lockReadOnly, site.id, updateSiteContext, triggerFlash]);

  const cancelContext = useCallback(() => {
    if (intentResetInFlightRef.current || manualSaveInFlightRef.current) return;
    setContextDraft(null);
    lastAutoSavedContextRef.current = undefined;
  }, []);

  // ── Unified save (flattened mount only) ─────────────────────────
  // SAVEBTN — when the panel is flattened into a host surface (the
  // Overview "Edit intent" editor mounts Project Intent + Site Context
  // together, the Report-preferences dialog mounts Report Preferences
  // alone), the per-section Cancel/Save bars become a confusing pile of
  // duplicate affordances ("we don't need 2 save buttons for intent and
  // context"). Replace them with ONE Cancel/Save footer that commits
  // every dirty section in a single action and shows a single toast.
  // Each underlying write is still the same store action that persisted
  // it before — no field is dropped; only the affordance is unified.
  const anyDirty = intentDirty || prefsDirty || contextDirty;

  const saveAll = useCallback(async () => {
    if (lockReadOnly || intentResetInFlightRef.current || manualSaveInFlightRef.current || autoSaveInFlightRef.current || saveAllInFlightRef.current) return;
    const subject = intentSubjectRef.current;
    // Snapshot which sections are dirty before any awaits clear them.
    const dirtySections: ExpandedKey[] = [
      intentDirty ? 'intent' : null,
      prefsDirty ? 'prefs' : null,
      contextDirty ? 'context' : null,
    ].filter((k): k is NonNullable<ExpandedKey> => k !== null);
    if (dirtySections.length === 0) return;
    saveAllInFlightRef.current = true;
    try {
      // Sequential so each store action's audit/Firestore write is
      // independent and ordering is deterministic. `silent` suppresses
      // the per-section toasts; we fire one consolidated toast below.
      if (intentDirty) await saveIntent({ silent: true });
      if (intentSubjectRef.current !== subject || !subject.mounted) return;
      if (prefsDirty) await savePrefs({ silent: true });
      if (intentSubjectRef.current !== subject || !subject.mounted) return;
      if (contextDirty) await saveContext({ silent: true });
      if (intentSubjectRef.current !== subject || !subject.mounted) return;
      toast.success(
        dirtySections.length === 1
          ? 'Site configuration saved'
          : `Site configuration saved (${dirtySections.length} sections)`,
      );
    } catch (e) {
      // A per-section silent save threw — surface one error toast. Any
      // section that already committed has cleared its own draft, so the
      // remaining dirty section(s) stay editable for a retry.
      if (intentSubjectRef.current === subject && subject.mounted) toast.error(`Couldn't save site configuration: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      saveAllInFlightRef.current = false;
    }
  }, [lockReadOnly, intentDirty, prefsDirty, contextDirty, saveIntent, savePrefs, saveContext]);

  const cancelAll = useCallback(() => {
    cancelIntent();
    cancelPrefs();
    cancelContext();
  }, [cancelIntent, cancelPrefs, cancelContext]);

  // ── PERSIST-ON-CHANGE — debounced auto-save (safety net) ────────────
  // The unified/per-section Save footers stay the canonical, immediate
  // commit. But power-mix MW/CF edits (and every other intent edit) live
  // in `intentDraft` until the user clicks Save — and a reload before
  // that click loses them. This effect persists the DRAFT verbatim
  // through the SAME store actions after a quiet period, so an unsaved
  // edit can never be lost on reload.
  //
  // Why it does NOT clear the draft (unlike `saveIntent`/`saveContext`):
  // the auto-save is a SAFETY NET, not a commit. Clearing the draft would
  // make the dirty dot / footer Save flicker off mid-edit and snap the
  // form to the round-tripped value. Instead we persist in place and bump
  // a per-section baseline so we never re-write an unchanged draft. The
  // explicit footer Save still owns the user-visible "saved" toast + the
  // draft-clear (returning the section to its pristine state).
  //
  // Guards (no thrash / no double-write / no lock-bypass):
  //   - debounce (AUTOSAVE_DEBOUNCE_MS) coalesces rapid keystrokes/pills;
  //   - skip when the draft is deep-equal (JSON identity) to the last
  //     value we persisted (baseline) — also catches a manual Save that
  //     already flushed the same value;
  //   - skip while a manual Save is in flight (the footer sets the ref),
  //     and never run two auto-saves concurrently;
  //   - skip entirely when `lockReadOnly` — the auto-save must respect the
  //     same lock the host enforces on the manual affordances;
  //   - a failed write does NOT clear the draft or advance the baseline,
  //     so the manual Save remains available. Flattened editors show the
  //     failed section and allow an explicit discard/reload. Another edit
  //     starts a new quiet period; a failure alone never loops a retry.
  const AUTOSAVE_DEBOUNCE_MS = 1200;

  const serializeIntent = useCallback(
    (i: ProjectIntent | null) => (i === null ? undefined : JSON.stringify(i)),
    [],
  );

  React.useEffect(() => {
    // Nothing dirty / locked ⇒ no auto-save. (Both drafts null ⇒ idle.)
    if (lockReadOnly || intentResetPending) return;
    if (intentDraft === null && contextDraft === null) return;
    const subject = intentSubjectRef.current;
    const isCurrentSubject = () => intentSubjectRef.current === subject && subject.mounted;

    const timer = setTimeout(() => {
      // Re-read guards at fire time — a manual Save may have started, or
      // the lock may have flipped, between schedule and fire.
      if (!isCurrentSubject() || lockReadOnly || intentResetInFlightRef.current) return;
      if (manualSaveInFlightRef.current || autoSaveInFlightRef.current || saveAllInFlightRef.current) return;

      const intentJson = serializeIntent(intentDraft);
      const contextJson = contextDraft === null ? undefined : JSON.stringify(contextDraft);
      const intentNeedsSave =
        intentDraft !== null && intentJson !== lastAutoSavedIntentRef.current;
      const contextNeedsSave =
        contextDraft !== null && contextJson !== lastAutoSavedContextRef.current;
      if (!intentNeedsSave && !contextNeedsSave) return;

      autoSaveInFlightRef.current = true;
      setAutoSaveStatus('saving');
      setAutoSaveError('');
      setFailedAutoSaveSection(null);
      void (async () => {
        let wrote = false;
        let attemptedSection: 'intent' | 'context' | null = null;
        try {
          // Persist the draft VERBATIM through the canonical store action,
          // WITHOUT clearing the draft (see header comment). Intent first
          // — it carries the power-mix the user is most likely mid-editing.
          if (intentNeedsSave && intentDraft !== null) {
            attemptedSection = 'intent';
            const recipe = phasePlanApplicationRef.current;
            await updateSiteProjectIntent(site.id, intentDraft, {
              expectedDecisionHash: intentDraftBaselineRef.current,
              phasePlanApplication: recipe,
            });
            if (!isCurrentSubject()) return;
            const saved = useStore.getState().siteRag.sites.find(candidate => candidate.id === site.id);
            if (saved) intentDraftBaselineRef.current = intentDecisionStateHash(saved);
            if (phasePlanApplicationRef.current === recipe) phasePlanApplicationRef.current = undefined;
            // Server attribution belongs to the adopted stamp. Retain newer
            // typing, while replacing an unchanged draft with that exact save.
            const savedIntent = saved?.projectIntent;
            if (savedIntent) setIntentDraft(previous => previous && hashGenerationImpactValue(previous) === hashGenerationImpactValue(intentDraft) ? savedIntent : previous);
            lastAutoSavedIntentRef.current = savedIntent ? serializeIntent(savedIntent) : intentJson;
            wrote = true;
            triggerFlash('intent');
          }
          if (contextNeedsSave && contextDraft !== null) {
            attemptedSection = 'context';
            await updateSiteContext(site.id, contextDraft);
            if (!isCurrentSubject()) return;
            lastAutoSavedContextRef.current = contextJson;
            wrote = true;
            triggerFlash('context');
          }
          // AUTOSAVE-STATUS — resolve the inline indicator. A successful
          // write stamps "Saved · {relative}"; a no-op (nothing needed
          // saving) returns to idle so the chip simply disappears.
          if (wrote) {
            setAutoSavedAt(Date.now());
            setAutoSaveStatus('saved');
          } else {
            setAutoSaveStatus('idle');
          }
        } catch (error) {
          // Keep the unsaved draft and its initiating baseline. The flattened
          // editor has no manual Save, so persistence errors must be visible.
          if (isCurrentSubject()) {
            setAutoSaveStatus('error');
            setAutoSaveError(error instanceof Error ? error.message : 'Could not save these changes.');
            setFailedAutoSaveSection(attemptedSection);
          }
        } finally {
          autoSaveInFlightRef.current = false;
          // A new site's quiet-period timer may have backed off while the old
          // write held this mutex. Re-arm that draft without copying A's saved
          // baseline/status to B or issuing a concurrent write.
          if (!isCurrentSubject() && intentSubjectRef.current.mounted) setAutoSaveRelease(version => version + 1);
        }
      })();
    }, AUTOSAVE_DEBOUNCE_MS);

    // Cancel the pending timer on every draft change (coalesce) and on
    // unmount (no orphaned write after the panel is gone).
    return () => clearTimeout(timer);
  }, [
    intentDraft,
    contextDraft,
    lockReadOnly,
    intentResetPending,
    autoSaveRelease,
    manualWritePending,
    site.id,
    updateSiteProjectIntent,
    updateSiteContext,
    triggerFlash,
    serializeIntent,
  ]);

  // Escape closes whichever section is open (cancels its draft per
  // section's `onCancel`). Mounted at the panel level so users get
  // the same affordance from any expanded section. Evidence Mode has
  // no draft state — switching modes commits immediately — so Escape
  // simply collapses the section.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (expanded === 'intent') cancelIntent();
      if (expanded === 'prefs') cancelPrefs();
      if (expanded === 'context') cancelContext();
      setExpanded(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, cancelIntent, cancelPrefs, cancelContext]);

  // Toggle a section: expanding a new section cancels the prior
  // section's unsaved draft to keep one source of truth (the user
  // explicitly chose to leave; Cancel reverts).
  const toggleSection = useCallback((key: NonNullable<ExpandedKey>) => {
    setExpanded(prev => (prev === key ? null : key));
  }, []);

  // ── Evidence Mode section ────────────────────────────────────────
  // 4th section in the panel — no draft state (writes commit through
  // EvidenceModeControl's own handler). Unstamped legacy sites default to
  // Datacrate Engine behaviour but only count as "set" once stamped.
  const evidenceMode = site.evidenceMode;
  const evidenceModeSet = Boolean(evidenceMode && evidenceMode !== 'awaiting');
  const evidenceModeLabel: string =
    evidenceMode === 'docs-included' ? 'Docs included'
    : evidenceMode === 'awaiting' ? 'Awaiting'
    : 'Datacrate Engine';

  const totalSetSections =
    (intentSetCount > 0 ? 1 : 0)
    + (prefsCustomizedCount > 0 ? 1 : 0)
    + (contextLen > 0 ? 1 : 0)
    + (evidenceModeSet ? 1 : 0);

  // Provenance footer. siteConfigUpdatedAt is stamped by each writer;
  // siteConfigUpdatedBy carries the actor uid/email.
  const provenanceLabel = useMemo(() => {
    if (!site.siteConfigUpdatedAt) {
      return 'Set up your site configuration to tailor every report ↓';
    }
    const who = site.siteConfigUpdatedBy?.email ?? site.siteConfigUpdatedBy?.uid ?? 'a teammate';
    return `Last updated by ${who} ${humanizeAgo(site.siteConfigUpdatedAt)}`;
  }, [site.siteConfigUpdatedAt, site.siteConfigUpdatedBy]);

  // AUTOSAVE-STATUS — relative label for the "Saved · {…}" inline indicator.
  // Recomputed when the last-auto-saved timestamp changes; the first minute
  // reads "just now" so the chip stays calm immediately after a write.
  const autoSavedRelativeLabel = useMemo(() => {
    if (autoSavedAt === null) return 'just now';
    const ageMs = Date.now() - autoSavedAt;
    if (!Number.isFinite(ageMs) || ageMs < 10_000) return 'just now';
    return humanizeAgo(new Date(autoSavedAt).toISOString()) || 'just now';
  }, [autoSavedAt]);

  const showSection = useCallback(
    (key: NonNullable<ExpandedKey>) => !visibleSections || visibleSections.includes(key),
    [visibleSections],
  );

  const renderSectionShell = useCallback((
    key: NonNullable<ExpandedKey>,
    section: React.ReactNode,
  ) => {
    if (!showSection(key)) return null;
    return section;
  }, [showSection]);

  // ── Declutter (2026-06-10) — Context-as-stepper-step-1 ──────────────
  // When the Intent stepper is active AND the host scopes BOTH the intent
  // and context sections to this panel (the Overview Intent tab does:
  // visibleSections={['intent','context']} + intentStepper), fold the Site
  // Context surface into the stepper as step 1 instead of rendering it as
  // its own collapsible section below. This avoids the context surface
  // appearing twice. The folded node is the SAME SiteContextInput element
  // the standalone section used (same value/onChange/context/onRetryExtract
  // wiring) PLUS `hideClaimList` so it reads as a silent prose surface —
  // extraction still runs via onRetryExtract / store-side blur. Because its
  // onChange still writes `contextDraft`, the unified Save footer persists
  // the text + extracted claims exactly as before.
  const foldContextIntoStepper = intentStepper && showSection('context') && showSection('intent');

  // Site context is edited here; the Map tab owns study-area selection and
  // land screening so Intent does not duplicate its evidence or controls.
  const contextStepNode = (
    <div className="space-y-4">
      <SiteContextInput
        value={contextText}
        onChange={(v) => setContextDraft(v)}
        context={site.siteContext}
        testIdPrefix={`${testIdPrefix}-context-input`}
        onRetryExtract={() => retryExtractClaims(site.id)}
        hideClaimList
      />
    </div>
  );

  return (
    <div className={hideShell ? '' : 'border border-border bg-card'} data-testid={testIdPrefix}>
      {!hideShell && (
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
            Site Configuration
          </h2>
          <span className="type-body uppercase tracking-widest text-text-muted">
            {totalSetSections} of 4 sections set
          </span>
        </div>
      )}

      <div className={hideShell ? '' : 'p-4 space-y-3'}>
        {/* ── Project Intent ───────────────────────────────────── */}
        {renderSectionShell('intent', (
        <CollapsibleSection
          flatten={flattenSections}
          testId={`${testIdPrefix}-intent`}
          anchorId="site-project-intent"
          title="Project Intent"
          expanded={expanded === 'intent'}
          onToggle={() => toggleSection('intent')}
          dirty={intentDirty}
          flashSaved={flashSaved === 'intent'}
          statusChip={
            intentSetCount > 0
              ? <StatusChip tone="set" label={`${intentSetCount} field${intentSetCount === 1 ? '' : 's'} set`} testId={`${testIdPrefix}-intent-chip`} />
              : <StatusChip tone="muted" label="Set up" testId={`${testIdPrefix}-intent-chip`} />
          }
          summary={
            <div className="space-y-1.5">
              {/* DR2 — `site.description` summary preview removed; the
                   brief now lives on `siteContext` as a `general/brief`
                   claim. The Site Context section below shows it. */}
              {intentSummary
                ? <span className="leading-relaxed">{intentSummary}</span>
                : (
                  <span className="text-text-muted italic">
                    {isScreeningRecommendationMode(intentValue)
                      ? 'Let screening recommend conditional size, power and cooling options. Project inputs are optional.'
                      : 'Screening accepts an approximate proposal or undecided inputs. Add detailed report inputs when known.'}
                  </span>
                )}
              {phasedView.isPhased && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowPhaseGantt(true); }}
                  data-testid={`${testIdPrefix}-intent-view-phase-gantt`}
                  className="block type-meta text-text-secondary hover:text-text-primary underline-offset-2 hover:underline"
                >
                  View phase timeline →
                </button>
              )}
            </div>
          }
        >
          {/* Declutter (2026-06-10):
              - Change 5 — removed the "Site description / Add site context →"
                breadcrumb blurb; the Context stepper step replaces it.
              - Change 4 — removed the workload long-form blurb + the
                "View what this changes" impact disclosure. The Workload
                profile PICKER itself stays (it lives inside ProjectIntentForm's
                workload step/band). */}
          {renderScreeningHeader && (
            <div
              className="mb-4 flex flex-wrap items-center gap-1 border-b border-border-subtle"
              role="tablist"
              aria-label="Intent views"
              data-testid={`${testIdPrefix}-subtabs`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={intentSubTab === 'intent'}
                onClick={() => setIntentSubTab('intent')}
                data-testid={`${testIdPrefix}-subtab-intent`}
                className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold transition-colors ${
                  intentSubTab === 'intent'
                    ? 'border-primary text-text-primary'
                    : 'border-transparent text-text-muted hover:text-text-primary'
                }`}
              >
                {intentInputStage === 'detailed' ? 'Design & report assumptions' : 'Project brief'}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={intentSubTab === 'recommended-path'}
                onClick={() => setIntentSubTab('recommended-path')}
                data-testid={`${testIdPrefix}-subtab-recommended-path`}
                className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold transition-colors ${
                  intentSubTab === 'recommended-path'
                    ? 'border-primary text-text-primary'
                    : 'border-transparent text-text-muted hover:text-text-primary'
                }`}
              >
                Recommended path
              </button>
            </div>
          )}
          {renderScreeningHeader && (
            <div
              className={intentSubTab === 'recommended-path' ? undefined : 'hidden'}
              data-testid={`${testIdPrefix}-recommended-path-panel`}
            >
              <fieldset disabled={lockReadOnly || intentResetPending || manualWritePending} className="mb-4 min-w-0">
                {renderScreeningHeader({ intent: intentValue ?? undecidedProjectIntent(), applyIntent: stageIntentEdit }) ?? (
                  intentSubTab === 'recommended-path' ? (
                    <p className="py-5 text-sm leading-7 text-text-secondary" data-testid={`${testIdPrefix}-recommended-path-empty`}>
                      No completed screening recommendation is available yet. Run the Screening Assessment in Analyses to review the recommended power, cooling, and sizing path here.
                    </p>
                  ) : null
                )}
              </fieldset>
            </div>
          )}
          <div
            className={renderScreeningHeader && intentSubTab !== 'intent' ? 'hidden' : undefined}
            data-testid={`${testIdPrefix}-intent-editor-panel`}
          >
          {intentInputStage === 'detailed' && (
            <div className="mb-4 space-y-2" data-testid={`${testIdPrefix}-design-assumptions-heading`}>
              <button type="button" className="text-sm text-primary hover:underline" onClick={() => setIntentInputStage('screening')}>
                ← Back to screening brief
              </button>
              <h3 className="type-meta font-semibold">Design &amp; report assumptions</h3>
              <p className="text-xs text-text-muted">Record known constraints or a design selected after reviewing recommendations. These inputs are optional for screening; each report collects its own additional setup.</p>
            </div>
          )}
          {intentInputStage === 'detailed' && !intentStepper && renderDesignHeader && (
            <fieldset disabled={lockReadOnly || intentResetPending || manualWritePending} className="mb-4 min-w-0">
              {renderDesignHeader({
                intent: intentValue ?? (site.draftOnly ? undecidedProjectIntent() : emptyProjectIntent()),
                applyIntent: stageIntentEdit,
              })}
            </fieldset>
          )}
          {intentInputStage === 'screening' && screeningNotice && (
            <div className="mb-4">{screeningNotice}</div>
          )}
          <fieldset disabled={lockReadOnly || intentResetPending || manualWritePending} className="min-w-0">
          <ProjectIntentForm
            inputStage={intentInputStage}
            onOpenDetailedPlanning={() => setIntentInputStage('detailed')}
            value={intentValue ?? (site.draftOnly ? undecidedProjectIntent() : emptyProjectIntent())}
            onChange={stageIntentEdit}
            onResetUndecided={resetIntentChoices}
            resetPending={intentResetPending}
            resetDisabled={manualWritePending || autoSaveStatus === 'saving'}
            readOnly={lockReadOnly}
            resetExplanation="Sets all Intent choices to undecided, clears the power strategy and Stage 2 acceptances, and deselects the current assessment. Saved assessments remain available to select again."
            onStepperDone={onIntentStepperDone}
            testIdPrefix={`${testIdPrefix}-intent-form`}
            phasePowerPlan={phasePowerPlan}
            phasePowerPlanStaleReason={phasePowerPlanStaleReason}
            onApplyPhasePowerPlan={handleApplyPhasePowerPlan}
            stepper={intentStepper}
            renderStepStageHeader={intentInputStage === 'detailed' && intentStepper && renderDesignHeader
              ? ({ activeStep, onSelectStep }) => renderDesignHeader({
                  intent: intentValue ?? (site.draftOnly ? undecidedProjectIntent() : emptyProjectIntent()),
                  applyIntent: stageIntentEdit,
                  activeStep,
                  onSelectStep: stepId => onSelectStep(stepId as typeof activeStep),
                })
              : undefined}
            // Declutter — when the stepper is active, fold the (silent) Site
            // Context surface in as step 1 via node-passing (mirrors how
            // lockControls is handed to the Home hero) so we don't deep-thread
            // context state. The standalone Site Context section below is
            // suppressed (see `foldContextIntoStepper`) to avoid duplication;
            // its draft is still saved by the unified Save footer because this
            // node's onChange writes the same `contextDraft` state.
            contextStep={foldContextIntoStepper ? contextStepNode : undefined}
            contextStepComplete={contextText.trim().length > 0}
            siteContext={site.siteContext ?? null}
            intentSuggestions={intentSuggestions}
            nextBestInput={nextBestInput}
            dismissedFields={dismissedIntentFields}
            // Accept merges the writer-stamped patch into the draft via the
            // canonical setIntentDraft path — NO new store action. The form
            // already stamped fieldSource = 'accepted' on the patch, so the
            // accepted value is never re-suggested (USER ALWAYS WINS).
            onAcceptSuggestion={(_field, next) => stageIntentEdit(next)}
            onDismissSuggestion={(field) =>
              setDismissedIntentFields(prev => {
                const nextSet = new Set(prev);
                nextSet.add(field);
                return nextSet;
              })
            }
          />
          </fieldset>
          </div>
          {intentResetMessage && <p role="status" className="mt-2 text-xs text-text-muted"
            data-testid={`${testIdPrefix}-intent-reset-status`}>{intentResetMessage}</p>}
          {/* SAVEBTN — per-section Cancel/Save only in the standalone
              (non-flattened) panel. Flattened mounts share the unified
              footer below so the user sees ONE Save for all sections. */}
          {!flattenSections && (
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border-subtle">
              <Button
                variant="outline"
                className="h-8 rounded-md type-meta"
                onClick={cancelIntent}
                disabled={!intentDirty || lockReadOnly || intentResetPending || manualWritePending}
                data-testid={`${testIdPrefix}-intent-cancel`}
              >
                Cancel
              </Button>
              <Button
                className="h-8 rounded-md type-meta"
                onClick={() => { void saveIntent().catch(() => {}); }}
                disabled={!intentDirty || lockReadOnly || intentResetPending || manualWritePending}
                data-testid={`${testIdPrefix}-intent-save`}
              >
                Save
              </Button>
            </div>
          )}
        </CollapsibleSection>
        ))}

        {/* ── Typed Preferences ────────────────────────────────── */}
        {renderSectionShell('prefs', (
        <CollapsibleSection
          flatten={flattenSections}
          testId={`${testIdPrefix}-prefs`}
          title="Report Preferences"
          expanded={expanded === 'prefs'}
          onToggle={() => toggleSection('prefs')}
          dirty={prefsDirty}
          flashSaved={flashSaved === 'prefs'}
          statusChip={
            prefsCustomizedCount > 0
              ? <StatusChip tone="set" label={`${prefsCustomizedCount} of ${REPORT_TYPE_ORDER.length} customized`} testId={`${testIdPrefix}-prefs-chip`} />
              : <StatusChip tone="muted" label="Using defaults" testId={`${testIdPrefix}-prefs-chip`} />
          }
          summary={
            <span className="text-text-secondary leading-relaxed">
              {prefsCustomizedCount > 0
                ? `${prefsCustomizedCount} report type${prefsCustomizedCount === 1 ? '' : 's'} customized.`
                : 'Per-report-type preferences. Customize to tailor any individual report flavour.'}
            </span>
          }
        >
          {/* R-131-OVERVIEW-UX-A — inline help for what Report Preferences affect. */}
          <div className="flex items-start justify-between gap-2 pb-2 border-b border-border-subtle">
            <p className="text-sm text-text-secondary leading-relaxed">
              Tune how each report type grades and narrates your site. Changes apply to every future report of that type.
            </p>
            <ContextualHelpButton
              articleSlug="reports"
              label="What is Report Variables?"
              className="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-full border border-border text-text-secondary hover:text-text-primary hover:bg-state-hover transition-colors"
            />
          </div>
          <div className="space-y-1" role="list">
            {REPORT_TYPE_ORDER.filter(rt => !filterReportTypes || filterReportTypes.includes(rt)).map(rt => {
              const Icon = REPORT_TYPE_ICON[rt];
              const meta = getReportType(rt);
              const slot = livePrefs[rt];
              const setCount = countSetPrefFields(slot);
              const isOpen = prefsRowExpanded === rt;
              return (
                <div
                  key={rt}
                  role="listitem"
                  className={`border border-border-subtle ${setCount > 0 ? 'border-l-2 border-l-accent' : ''}`}
                  data-testid={`${testIdPrefix}-prefs-row-${rt}`}
                  data-customized={setCount > 0 ? 'true' : 'false'}
                >
                  <button
                    type="button"
                    onClick={() => setPrefsRowExpanded(prev => (prev === rt ? null : rt))}
                    aria-expanded={isOpen}
                    className="group w-full flex items-center gap-3 px-3 py-2 hover:bg-state-hover text-left transition-colors"
                  >
                    <Icon className="w-3.5 h-3.5 text-text-secondary shrink-0" />
                    <span className="text-sm font-semibold text-text-primary truncate flex-1">
                      {meta.shortLabel}
                    </span>
                    {setCount > 0
                      ? <StatusChip tone="set" label={`${setCount} field${setCount === 1 ? '' : 's'} set`} />
                      : <StatusChip tone="muted" label="Using defaults" />}
                    {isOpen
                      ? <ChevronDown className="w-3.5 h-3.5 text-text-muted shrink-0 group-hover:text-text-primary transition-colors" />
                      : <ChevronRight className="w-3.5 h-3.5 text-text-muted shrink-0 group-hover:text-text-primary transition-colors" />}
                  </button>
                  {isOpen && (
                    <fieldset disabled={lockReadOnly || intentResetPending || manualWritePending} className="min-w-0 px-3 pb-3 pt-2 border-t border-border-subtle">
                      <PreferencesFormFor
                        reportType={rt}
                        value={slot as never}
                        onChange={(next) => {
                          if (lockReadOnly || intentResetInFlightRef.current || manualSaveInFlightRef.current) return;
                          setPrefsDraft(prev => {
                            const base = prev ?? site.typedPreferencesDefaults ?? {};
                            return { ...base, [rt]: next } as TypedPreferencesByType;
                          });
                        }}
                      />
                    </fieldset>
                  )}
                </div>
              );
            })}
          </div>
          {!flattenSections && (
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border-subtle">
              <Button
                variant="outline"
                className="h-8 rounded-md type-meta"
                onClick={cancelPrefs}
                disabled={!prefsDirty || lockReadOnly || intentResetPending || manualWritePending}
                data-testid={`${testIdPrefix}-prefs-cancel`}
              >
                Cancel
              </Button>
              <Button
                className="h-8 rounded-md type-meta"
                onClick={() => { void savePrefs().catch(() => {}); }}
                disabled={!prefsDirty || lockReadOnly || intentResetPending || manualWritePending}
                data-testid={`${testIdPrefix}-prefs-save`}
              >
                Save
              </Button>
            </div>
          )}
        </CollapsibleSection>
        ))}

        {/* ── Site Context ─────────────────────────────────────── */}
        {/* Declutter — suppress the standalone Site Context section when it
            is folded into the Intent stepper as step 1 (avoids rendering the
            context surface twice). The folded node still writes contextDraft,
            so the unified Save footer persists it. */}
        {!foldContextIntoStepper && renderSectionShell('context', (
        <CollapsibleSection
          flatten={flattenSections}
          testId={`${testIdPrefix}-context`}
          anchorId="site-context"
          title="Site Context"
          expanded={expanded === 'context'}
          onToggle={() => toggleSection('context')}
          dirty={contextDirty}
          flashSaved={flashSaved === 'context'}
          statusChip={
            contextLen > 0
              ? <StatusChip tone={contextSetTone} label={`${contextLen} chars set`} testId={`${testIdPrefix}-context-chip`} />
              : <StatusChip tone="muted" label="Empty" testId={`${testIdPrefix}-context-chip`} />
          }
          summary={
            <div className="space-y-1">
              {site.siteContext?.text ? (
                <p
                  className="text-text-secondary leading-relaxed whitespace-pre-line"
                  style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                  {site.siteContext.text}
                </p>
              ) : (
                <span className="text-text-muted italic">
                  Tell the AI what you already know about this site (technology, prior studies, commitments, constraints). Drives the highest-priority context block in every report.
                </span>
              )}
              {(contextClaimCount > 0 || contextUnacknowledgedConflicts > 0) && (
                <div className="flex items-center gap-3 type-body uppercase tracking-widest text-text-muted">
                  {contextClaimCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> {contextClaimCount} claim{contextClaimCount === 1 ? '' : 's'} extracted
                    </span>
                  )}
                  {contextUnacknowledgedConflicts > 0 && (
                    <span className="inline-flex items-center gap-1 text-warning">
                      <AlertTriangle className="w-3 h-3" /> {contextUnacknowledgedConflicts} SED conflict{contextUnacknowledgedConflicts === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
              )}
            </div>
          }
        >
          <SiteContextInput
            value={contextText}
            onChange={(v) => setContextDraft(v)}
            context={site.siteContext}
            testIdPrefix={`${testIdPrefix}-context-input`}
            onRetryExtract={() => retryExtractClaims(site.id)}
          />
          {!flattenSections && (
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border-subtle">
              <Button
                variant="outline"
                className="h-8 rounded-md type-meta"
                onClick={cancelContext}
                disabled={!contextDirty || lockReadOnly || intentResetPending || manualWritePending}
                data-testid={`${testIdPrefix}-context-cancel`}
              >
                Cancel
              </Button>
              <Button
                className="h-8 rounded-md type-meta"
                onClick={() => { void saveContext().catch(() => {}); }}
                disabled={!contextDirty || lockReadOnly || intentResetPending || manualWritePending}
                data-testid={`${testIdPrefix}-context-save`}
              >
                Save
              </Button>
            </div>
          )}
        </CollapsibleSection>
        ))}

        {/* ── Evidence Mode (SC3-1 → SET-M3) ──────────────────────────
            SET-M3 — the editable EvidenceModeControl is removed from
            the Overview. Evidence Mode was mounted on BOTH this panel
            AND the Site Evidence tab (`SiteLandingPage.tsx`), giving
            two homes for a single-source-of-truth setting. Drift was
            documented in the SET pack's "Findings before you start"
            block. The Site Evidence tab remains the single editor.
            Here we keep the section header + status chip for at-a-
            glance state ("Currently set to {label}") and link the
            user to the canonical editor with "Change in Site
            Evidence →" when a host provides `onJumpToEvidenceTab`.
            Body is read-only — no draft state, no setSiteEvidenceMode
            wiring on this surface. */}
        {renderSectionShell('evidence', (
        <CollapsibleSection
          flatten={flattenSections}
          testId={`${testIdPrefix}-evidence`}
          title="Evidence Mode"
          expanded={expanded === 'evidence'}
          onToggle={() => toggleSection('evidence')}
          dirty={false}
          flashSaved={flashSaved === 'evidence'}
          statusChip={
            evidenceModeSet
              ? <StatusChip tone="set" label={evidenceModeLabel} testId={`${testIdPrefix}-evidence-chip`} />
              : <StatusChip tone="muted" label="Awaiting" testId={`${testIdPrefix}-evidence-chip`} />
          }
          summary={
            <span className="text-text-secondary leading-relaxed">
              {evidenceModeSet
                ? `Currently set to ${evidenceModeLabel}.`
                : <span className="italic">Defaults to Datacrate Engine — choose explicitly in the Site Evidence tab.</span>}
            </span>
          }
        >
          <div
            className="flex items-center justify-between gap-3 type-meta"
            data-testid={`${testIdPrefix}-evidence-readonly`}
          >
            <span>
              Evidence mode: <strong className="text-text-primary">{evidenceModeLabel}</strong>
            </span>
            {onJumpToEvidenceTab && (
              <button
                type="button"
                onClick={onJumpToEvidenceTab}
                className="text-primary hover:underline type-meta"
                data-testid={`${testIdPrefix}-evidence-jump`}
              >
                Change in Site Evidence →
              </button>
            )}
          </div>
        </CollapsibleSection>
        ))}

        {/* FOOTER — flattened mounts.
            • Intent surface (Project Intent + Site Context, the Overview
              Intent tab): the debounced persist-on-change auto-save now
              commits every intent + context draft field on its own, so the
              old manual Save/Cancel pair was redundant (aurora-qa-4). It is
              REPLACED by an unobtrusive auto-save status line — "Saving…"
              while a debounced write is in flight, "Saved · {relative}" once
              it resolves, the read-only label when the site is locked, and
              nothing while idle.
            • Report-Preferences dialog (`visibleSections=['prefs']`): typed
              preferences are NOT covered by the auto-save net, so that mount
              KEEPS the unified Save/Cancel footer that commits the dirty
              prefs slots. */}
        {flattenSections && showSection('prefs') && (
          <div
            className="flex items-center justify-end gap-2 pt-2 mt-1 border-t border-border-subtle"
            data-testid={`${testIdPrefix}-save-bar`}
          >
            <Button
              variant="outline"
              className="h-8 rounded-md type-meta"
              onClick={cancelAll}
              disabled={!anyDirty || lockReadOnly || intentResetPending || manualWritePending}
              data-testid={`${testIdPrefix}-cancel`}
            >
              Cancel
            </Button>
            <Button
              className="h-8 rounded-md type-meta"
              onClick={() => { void saveAll(); }}
              disabled={!anyDirty || lockReadOnly || intentResetPending || manualWritePending}
              data-testid={`${testIdPrefix}-save`}
            >
              Save
            </Button>
          </div>
        )}
        {flattenSections && !showSection('prefs') && (
          <div
            className="flex flex-wrap items-center justify-end gap-2 pt-2 mt-1 border-t border-border-subtle min-h-[1.5rem]"
            data-testid={`${testIdPrefix}-save-bar`}
          >
            <span
              className="type-meta text-text-muted"
              aria-live="polite"
              data-testid={`${testIdPrefix}-autosave-status`}
              data-autosave-state={lockReadOnly ? 'readonly' : autoSaveStatus}
            >
              {lockReadOnly
                ? 'Read-only'
                : autoSaveStatus === 'saving'
                  ? 'Saving…'
                  : autoSaveStatus === 'saved'
                    ? `Saved · ${autoSavedRelativeLabel}`
                    : ''}
            </span>
            {!lockReadOnly && autoSaveStatus === 'error' && (
              <div className="w-full text-sm text-warning" role="alert">
                <p>Changes are not saved. {autoSaveError}</p>
                <button type="button" className="mt-1 underline underline-offset-4" onClick={() => {
                  if (failedAutoSaveSection === 'context') cancelContext();
                  else cancelIntent();
                  setAutoSaveStatus('idle'); setAutoSaveError(''); setFailedAutoSaveSection(null); void fetchSites();
                }}>Discard {failedAutoSaveSection === 'context' ? 'context' : 'Intent'} draft and reload saved inputs</button>
              </div>
            )}
          </div>
        )}
      </div>

      {!hideShell && (
      <div
        className="px-4 py-2 border-t border-border text-[10px] italic text-text-muted"
        data-testid={`${testIdPrefix}-provenance`}
        title={site.siteConfigUpdatedAt ? new Date(site.siteConfigUpdatedAt).toLocaleString() : undefined}
      >
        {provenanceLabel}
      </div>
      )}
      {/* PP4 — phase Gantt modal. Only rendered when the user clicks
          "View phase timeline →" from the collapsed Intent summary. */}
      <PhaseGanttModal
        intent={intentValue}
        open={showPhaseGantt}
        onClose={() => setShowPhaseGantt(false)}
        testIdPrefix={`${testIdPrefix}-intent-phase-gantt`}
      />
    </div>
  );
};

export default SiteConfigurationPanel;
